/* eslint-disable import/no-unresolved */

import { provide } from 'vue';

import { createVueApp } from '@vuejs/entry';

import SharedOffcanvasApp from '@vuejs/apps/SharedOffcanvasApp.vue';
import SharedModalApp from '@vuejs/apps/SharedModalApp.vue';
import SharedPopupApp from '@vuejs/apps/SharedPopupApp.vue';
import ToastApp from '@vuejs/apps/ToastApp.vue';
import { mountProductBadges } from '@vuejs/helpers/badgesMount';
import { mountPricesWithCode } from '@vuejs/helpers/priceWithCodeMount';
// import CategoryFiltersApp from '@vuejs/apps/CategoryFiltersApp.vue';

import { sharedPinia } from '@vuejs/pinia';

import lozad from 'lozad';

/**
 * Aplikacje ładowane globalnie
 */
document.addEventListener('DOMContentLoaded', async () => {
    const sharedOffcanvasApp = createVueApp(SharedOffcanvasApp);
    const sharedModalApp = createVueApp(SharedModalApp);
    const sharedPopupApp = createVueApp(SharedPopupApp);
    const toastApp = createVueApp(ToastApp);

    sharedOffcanvasApp.use(sharedPinia);
    sharedModalApp.use(sharedPinia);
    sharedPopupApp.use(sharedPinia);
    toastApp.use(sharedPinia);

    sharedOffcanvasApp.mount('#shared-offcanvas-app');
    sharedModalApp.mount('#shared-modal-app');
    sharedPopupApp.mount('#shared-popup-app');
    toastApp.mount('#toast-app');
});

/**
 * Aplikacje ładowanie na odpowiednich podstronach
 */
window.addEventListener('page-loaded.category', async () => {
    const CategoryFiltersApp = await import(
        /** webpackChunkName: 'CategoryFiltersApp' */ '@vuejs/apps/CategoryFiltersApp.vue'
    );
    const categoryFiltersApp = createVueApp(CategoryFiltersApp.default);

    categoryFiltersApp.use(sharedPinia);

    categoryFiltersApp.mount('#category-filters-app');

    // Active filters app (desktop)
    const ActiveFiltersApp = await import(
        /** webpackChunkName: 'ActiveFiltersApp' */ '@vuejs/apps/ActiveFiltersApp.vue'
    );
    const activeFiltersAppElement = document.querySelector('#active-filters-app');
    if (activeFiltersAppElement) {
        const activeFiltersApp = createVueApp(ActiveFiltersApp.default);
        activeFiltersApp.use(sharedPinia);
        activeFiltersApp.mount(activeFiltersAppElement);
    }

    // Active filters scrollable badges (mobile)
    const ActiveFiltersBadgesScrollableApp = await import(
        /** webpackChunkName: 'ActiveFiltersBadgesScrollableApp' */ '@vuejs/apps/ActiveFiltersBadgesScrollableApp.vue'
    );
    const activeFiltersBadgesScrollableAppElement = document.querySelector(
        '#active-filters-scrollable-app'
    );
    if (activeFiltersBadgesScrollableAppElement) {
        const activeFiltersBadgesScrollableApp = createVueApp(
            ActiveFiltersBadgesScrollableApp.default
        );
        activeFiltersBadgesScrollableApp.use(sharedPinia);
        activeFiltersBadgesScrollableApp.mount(activeFiltersBadgesScrollableAppElement);
    }

    // Active filters count badge (mobile)
    const ActiveFiltersCountApp = await import(
        /** webpackChunkName: 'ActiveFiltersCountApp' */ '@vuejs/apps/ActiveFiltersCountApp.vue'
    );
    const activeFiltersCountAppElement = document.querySelector('#active-filters-badge-mobile');
    if (activeFiltersCountAppElement) {
        const activeFiltersCountApp = createVueApp(ActiveFiltersCountApp.default);
        activeFiltersCountApp.use(sharedPinia);
        activeFiltersCountApp.mount(activeFiltersCountAppElement);
    }
});

window.addEventListener('page-loaded.checkout', async () => {
    const CheckoutApp = await import(
        /** webpackChunkName: 'CheckoutApp' */ '@vuejs/apps/CheckoutApp.vue'
    );
    const { checkoutRouter } = await import(
        /** webpackChunkName: 'checkoutRouter' */ '@vuejs/router/checkout'
    );
    const { useAuthStore } = await import(/** webpackChunkName: 'authStore' */ '@vuejs/store/auth');

    const checkoutApp = createVueApp(CheckoutApp.default);
    const authStore = useAuthStore();

    checkoutApp.use(sharedPinia);
    checkoutApp.use(checkoutRouter);

    checkoutApp.mount('#checkout-app');

    authStore.me();
});

window.addEventListener('page-loaded.account', async () => {
    const AccountApp = await import(
        /** webpackChunkName: 'AccountApp' */ '@vuejs/apps/AccountApp.vue'
    );
    const { accountRouter } = await import(
        /** webpackChunkName: 'accountRouter' */ '@vuejs/router/account'
    );

    const accountApp = createVueApp(AccountApp.default);

    accountApp.use(sharedPinia);
    accountApp.use(accountRouter);

    accountApp.mount('#account-app');
});

window.addEventListener('page-loaded.checkout-success', async () => {
    const CheckoutSuccessApp = await import(
        /** webpackChunkName: 'CheckoutSuccessApp' */ '@vuejs/apps/CheckoutSuccessApp.vue'
    );

    const checkoutSuccessApp = createVueApp(CheckoutSuccessApp.default);

    checkoutSuccessApp.mount('#checkout-success-app');
});

window.addEventListener('page-loaded.success-charity', async () => {
    const SuccessCharityApp = await import(
        /** webpackChunkName: 'SuccessCharityApp' */ '@vuejs/apps/SuccessCharityApp.vue'
    );

    const successCharityApp = createVueApp(SuccessCharityApp.default);

    successCharityApp.mount('#success-charity-app');
});

const mountPaginationApps = async () => {
    const productsListGoNextPageAppElement = document.querySelector(
        '.js-products-list-go-next-page-app'
    );

    // eslint-disable-next-line no-underscore-dangle
    if (productsListGoNextPageAppElement && !productsListGoNextPageAppElement.__vue_app__) {
        const ProductsListGoNextPageApp = await import(
            /* webpackChunkName: "ProductsListGoNextPageApp" */ '@vuejs/apps/ProductsListGoNextPageApp.vue'
        );

        const productsListGoNextPageApp = createVueApp(ProductsListGoNextPageApp.default);

        productsListGoNextPageApp.use(sharedPinia);

        productsListGoNextPageApp.mount(productsListGoNextPageAppElement);
    }

    const productsListGoPreviousPageAppElement = document.querySelector(
        '.js-products-list-go-previous-page-app'
    );

    // eslint-disable-next-line no-underscore-dangle
    if (productsListGoPreviousPageAppElement && !productsListGoPreviousPageAppElement.__vue_app__) {
        const ProductsListGoPreviousPageApp = await import(
            /* webpackChunkName: "ProductsListGoPreviousPageApp" */ '@vuejs/apps/ProductsListGoPreviousPageApp.vue'
        );

        const productsListGoPreviousPageApp = createVueApp(ProductsListGoPreviousPageApp.default);

        productsListGoPreviousPageApp.use(sharedPinia);

        productsListGoPreviousPageApp.mount(productsListGoPreviousPageAppElement);
    }

    const productsListCountAppElements = document.querySelectorAll('.products-list-count-app');

    if (productsListCountAppElements.length) {
        const ProductsListCountApp = await import(
            /* webpackChunkName: "ProductsListCountApp" */ '@vuejs/apps/ProductsListCountApp.vue'
        );

        productsListCountAppElements.forEach((el) => {
            // eslint-disable-next-line no-underscore-dangle
            if (el.__vue_app__) return;
            const productsListCountApp = createVueApp(ProductsListCountApp.default);

            productsListCountApp.use(sharedPinia);

            productsListCountApp.mount(el);
        });
    }
};

/**
 * Aplikacje ładowane on-demand
 */
window.addEventListener('DOMContentLoaded', async () => {
    const TopbarApp = await import(/* webpackChunkName: "TopbarApp" */ '@vuejs/apps/TopbarApp.vue');

    const topbarElements = document.querySelectorAll('.js-vue-topbar');

    topbarElements.forEach((el) => {
        const topbarApp = createVueApp(TopbarApp.default);

        topbarApp.use(sharedPinia);
        topbarApp.provide('topbar', JSON.parse(el.dataset.topbar));

        topbarApp.mount(el);
    });

    const temporarySearchAppElements = document.querySelectorAll('.temporary-search-app');

    temporarySearchAppElements.forEach(async (el) => {
        const TemporarySearchApp = await import(
            /* webpackChunkName: "TemporarySearchApp" */ '@vuejs/apps/TemporarySearchApp.vue'
        );

        const temporarySearchApp = createVueApp(TemporarySearchApp.default);

        temporarySearchApp.use(sharedPinia);

        temporarySearchApp.mount(el);
    });

    // if (document.querySelector('#mobile-search-app')) {
    //     const MobileSearchApp = await import(
    //         /* webpackChunkName: "MobileSearchApp" */ '@vuejs/apps/MobileSearchApp.vue'
    //     );

    //     const mobileSearchApp = createVueApp(MobileSearchApp.default);

    //     mobileSearchApp.use(sharedPinia);

    //     mobileSearchApp.mount('#mobile-search-app');
    // }

    // if (document.querySelector('#desktop-search-app')) {
    //     const DesktopSearchApp = await import(
    //         /* webpackChunkName: "DesktopSearchApp" */ '@vuejs/apps/DesktopSearchApp.vue'
    //     );

    //     const desktopSearchApp = createVueApp(DesktopSearchApp.default);

    //     desktopSearchApp.use(sharedPinia);

    //     desktopSearchApp.mount('#desktop-search-app');
    // }

    // if (document.querySelector('#search-app')) {
    //     const SearchApp = await import(
    //         /* webpackChunkName: "SearchApp" */ '@vuejs/apps/SearchApp.vue'
    //     );

    //     const searchApp = createVueApp(SearchApp.default);

    //     searchApp.use(sharedPinia);

    //     searchApp.mount('#search-app');
    // }

    if (document.querySelector('#opinion-poll-app')) {
        const OpinionPollApp = await import(
            /* webpackChunkName: "OpinionPollApp" */ '@vuejs/apps/OpinionPollApp.vue'
        );

        const element = document.querySelector('#opinion-poll-app');

        const opinionPollApp = createVueApp(OpinionPollApp.default);

        opinionPollApp.use(sharedPinia);
        opinionPollApp.provide('consultants', JSON.parse(element?.dataset?.consultants || '[]'));
        opinionPollApp.provide('marketId', element?.dataset?.marketId);

        opinionPollApp.mount(element);
    }

    // window.PROMOTION.top_bars.forEach((topbar) => {
    //     const topbarApp = createVueApp(TopbarApp.default);
    //     const targetElement = document.querySelector('#js-mount-topbars-after-this-element');
    //     const mountElement = document.createElement('div');
    //     targetElement.parentNode.insertBefore(mountElement, targetElement.nextSibling);

    //     topbarApp.use(sharedPinia);
    //     topbarApp.provide('topbar', topbar);

    //     topbarApp.mount(mountElement);
    // });

    // if (document.querySelector('#topbar-app')) {
    //     const TopbarApp = await import(
    //         /* webpackChunkName: "TopbarApp" */ '@vuejs/apps/TopbarApp.vue'
    //     );

    //     const topbarApp = createVueApp(TopbarApp.default);

    //     topbarApp.use(sharedPinia);

    //     topbarApp.mount('#topbar-app');
    // }

    // if (document.querySelector('#header-app')) {
    //     const HeaderApp = await import(
    //         /* webpackChunkName: "NewsletterApp" */ '@vuejs/apps/HeaderApp.vue'
    //     );

    //     const headerApp = createVueApp(HeaderApp.default);

    //     headerApp.use(sharedPinia);

    //     headerApp.mount('#header-app');
    // }
    if (document.querySelector('#newsletter-app')) {
        const NewsletterApp = await import(
            /* webpackChunkName: "NewsletterApp" */ '@vuejs/apps/NewsletterApp.vue'
        );

        const newsletterApp = createVueApp(NewsletterApp.default);

        newsletterApp.use(sharedPinia);

        newsletterApp.mount('#newsletter-app');
    }

    if (document.querySelector('#exit-popup-lead-app')) {
        const ExitPopupLeadApp = await import(
            /* webpackChunkName: "ExitPopupLeadApp" */ '@vuejs/apps/ExitPopupLeadApp.vue'
        );

        const exitPopupLeadApp = createVueApp(ExitPopupLeadApp.default);

        exitPopupLeadApp.use(sharedPinia);

        exitPopupLeadApp.mount('#exit-popup-lead-app');
    }

    if (document.querySelector('#ebook-page-app')) {
        const EbookPageApp = await import(
            /* webpackChunkName: "EbookPageApp" */ '@vuejs/apps/EbookPageApp.vue'
        );

        const ebookPageApp = createVueApp(EbookPageApp.default);

        ebookPageApp.use(sharedPinia);

        ebookPageApp.mount('#ebook-page-app');
    }

    if (document.querySelector('#ebook-app')) {
        const EbookApp = await import(
            /* webpackChunkName: "EbookApp" */ '@vuejs/apps/EbookApp.vue'
        );

        const ebookApp = createVueApp(EbookApp.default);

        ebookApp.use(sharedPinia);

        ebookApp.mount('#ebook-app');
    }

    // const productGalleryDesktopAppElement = document.querySelector('#product-gallery-desktop-app');

    // if (productGalleryDesktopAppElement) {
    //     const ProductGalleryDesktopApp = await import(
    //         /* webpackChunkName: "ProductGalleryDesktopApp" */ '@vuejs/apps/product/ProductGalleryDesktopApp.vue'
    //     );

    //     const productGalleryDesktopApp = createVueApp(ProductGalleryDesktopApp.default, {
    //         globalState: {
    //             productDTO: JSON.parse(productGalleryDesktopAppElement.dataset.productDto),
    //             stoneInfo: JSON.parse(productGalleryDesktopAppElement.dataset.stoneInfo),
    //         },
    //     });

    //     productGalleryDesktopApp.use(sharedPinia);

    //     productGalleryDesktopApp.mount(productGalleryDesktopAppElement);
    // }

    if (document.querySelector('#ring-sets-app')) {
        const RingSetsApp = await import(
            /* webpackChunkName: "EbookApp" */ '@vuejs/apps/RingSetsApp.vue'
        );

        const ringSetsApp = createVueApp(RingSetsApp.default);

        ringSetsApp.use(sharedPinia);

        ringSetsApp.mount('#ring-sets-app');
    }

    if (document.querySelector('#product-variants-app')) {
        const ProductVariantsApp = await import(
            /* webpackChunkName: "ProductVariantsApp" */ '@vuejs/apps/product/ProductVariantsApp.vue'
        );

        const productVariantsApp = createVueApp(ProductVariantsApp.default);

        productVariantsApp.use(sharedPinia);

        const productVariantsContainer = document.querySelector('#product-variants-app');
        const productVariantsMountPoint =
            productVariantsContainer.querySelector('[data-vue-mount]') || productVariantsContainer;
        productVariantsApp.mount(productVariantsMountPoint);
    }

    if (document.querySelector('#product-variants-badges-app')) {
        const ProductVariantsBadgesApp = await import(
            /* webpackChunkName: "ProductVariantsBadgesApp" */ '@vuejs/apps/product/ProductVariantsBadgesApp.vue'
        );

        const productVariantsBadgesApp = createVueApp(ProductVariantsBadgesApp.default);

        productVariantsBadgesApp.use(sharedPinia);

        productVariantsBadgesApp.mount('#product-variants-badges-app');
    }

    if (document.querySelector('#product-variants-badges-v2-app')) {
        const ProductVariantsBadgesV2App = await import(
            /* webpackChunkName: "ProductVariantsBadgesV2App" */ '@vuejs/apps/product/ProductVariantsBadgesV2App.vue'
        );

        const productVariantsBadgesV2App = createVueApp(ProductVariantsBadgesV2App.default);

        productVariantsBadgesV2App.use(sharedPinia);

        productVariantsBadgesV2App.mount('#product-variants-badges-v2-app');
    }

    if (document.querySelector('#individual-offer-app')) {
        const IndividualOfferApp = await import(
            /* webpackChunkName: "IndividualOfferApp" */ '@vuejs/apps/IndividualOfferApp.vue'
        );

        const individualOfferApp = createVueApp(IndividualOfferApp.default);

        individualOfferApp.use(sharedPinia);

        individualOfferApp.mount('#individual-offer-app');
    }

    if (document.querySelector('#diamond-purchase-app')) {
        const DiamondPurchaseApp = await import(
            /* webpackChunkName: "ReadyProductsApp" */ '@vuejs/apps/product/DiamondPurchaseApp.vue'
        );

        const sylgemsApp = createVueApp(DiamondPurchaseApp.default);

        sylgemsApp.use(sharedPinia);

        sylgemsApp.mount('#diamond-purchase-app');
    }

    // AvailabilityStatusesApp nie ma juz wlasnego punktu montowania - renderuje ja
    // BuyboxApp (razem z paskiem odbioru w salonie), nad separatorem "lub".

    const fuckRakAppElement = document.querySelector('#fuck-rak-app');

    if (fuckRakAppElement) {
        const FuckRakBuyboxApp = await import(
            /* webpackChunkName: "FuckRakBuyboxApp" */ '@vuejs/apps/product/FuckRakBuyboxApp.vue'
        );

        const fuckRakApp = createVueApp(FuckRakBuyboxApp.default);
        fuckRakApp.use(sharedPinia);

        const fuckRakMountPoint =
            fuckRakAppElement.querySelector('[data-vue-mount]') || fuckRakAppElement;
        fuckRakApp.mount(fuckRakMountPoint);
    }

    const lapkiAppElement = document.querySelector('#lapki-app');

    if (lapkiAppElement) {
        const LapkiBuyboxApp = await import(
            /* webpackChunkName: "LapkiBuyboxApp" */ '@vuejs/apps/product/LapkiBuyboxApp.vue'
        );

        const lapkiApp = createVueApp(LapkiBuyboxApp.default);
        lapkiApp.use(sharedPinia);

        const lapkiMountPoint =
            lapkiAppElement.querySelector('[data-vue-mount]') || lapkiAppElement;
        lapkiApp.mount(lapkiMountPoint);
    }

    const lapkiFinalCtaAppElement = document.querySelector('#lapki-final-cta-app');

    if (lapkiFinalCtaAppElement) {
        const LapkiFinalCtaApp = await import(
            /* webpackChunkName: "LapkiFinalCtaApp" */ '@vuejs/apps/product/LapkiFinalCtaApp.vue'
        );

        const lapkiFinalCtaApp = createVueApp(LapkiFinalCtaApp.default);
        lapkiFinalCtaApp.use(sharedPinia);

        const lapkiFinalCtaMountPoint =
            lapkiFinalCtaAppElement.querySelector('[data-vue-mount]') || lapkiFinalCtaAppElement;
        lapkiFinalCtaApp.mount(lapkiFinalCtaMountPoint);
    }

    const fuckRakWaitlistAppElement = document.querySelector('#fuck-rak-waitlist-app');

    if (fuckRakWaitlistAppElement) {
        const FuckRakWaitlistApp = await import(
            /* webpackChunkName: "FuckRakWaitlistApp" */ '@vuejs/apps/product/FuckRakWaitlistApp.vue'
        );

        const fuckRakWaitlistApp = createVueApp(FuckRakWaitlistApp.default);
        fuckRakWaitlistApp.use(sharedPinia);

        const fuckRakWaitlistMountPoint =
            fuckRakWaitlistAppElement.querySelector('[data-vue-mount]') ||
            fuckRakWaitlistAppElement;
        fuckRakWaitlistApp.mount(fuckRakWaitlistMountPoint);
    }

    const fuckRakCheckoutAppElement = document.querySelector('#fuck-rak-checkout-app');

    if (fuckRakCheckoutAppElement) {
        const FuckRakCheckoutApp = await import(
            /* webpackChunkName: "FuckRakCheckoutApp" */ '@vuejs/apps/product/FuckRakCheckoutApp.vue'
        );

        const fuckRakCheckoutApp = createVueApp(FuckRakCheckoutApp.default);
        fuckRakCheckoutApp.use(sharedPinia);

        const fuckRakCheckoutMountPoint =
            fuckRakCheckoutAppElement.querySelector('[data-vue-mount]') ||
            fuckRakCheckoutAppElement;
        fuckRakCheckoutApp.mount(fuckRakCheckoutMountPoint);
    }

    const fuckRakInterestAppElement = document.querySelector('#fuck-rak-interest-app');

    if (fuckRakInterestAppElement) {
        const FuckRakInterestApp = await import(
            /* webpackChunkName: "FuckRakInterestApp" */ '@vuejs/apps/product/FuckRakInterestApp.vue'
        );

        const fuckRakInterestApp = createVueApp(FuckRakInterestApp.default);
        fuckRakInterestApp.use(sharedPinia);

        const fuckRakInterestMountPoint =
            fuckRakInterestAppElement.querySelector('[data-vue-mount]') ||
            fuckRakInterestAppElement;
        fuckRakInterestApp.mount(fuckRakInterestMountPoint);
    }

    const buyBoxAppElement = document.querySelector('#buybox-app');

    if (buyBoxAppElement) {
        // Nowa karta (lifting OB-1822) tylko przy wlaczniku NEW_PDP_ENABLED (window.IS_NEW_PDP),
        // reszta na starym buyboxie
        const loadBuyboxApp = () =>
            import(/* webpackChunkName: "BuyboxApp" */ '@vuejs/apps/product/BuyboxApp.vue');
        const loadBuyboxLegacyApp = () =>
            import(
                /* webpackChunkName: "BuyboxLegacyApp" */ '@vuejs/apps/product/BuyboxLegacyApp.vue'
            );
        const BuyboxApp = await (window.IS_NEW_PDP ? loadBuyboxApp() : loadBuyboxLegacyApp());

        const buyboxApp = createVueApp(BuyboxApp.default, {
            globalState: {
                productDTO: window.PRODUCT_DTO,
            },
        });

        buyboxApp.use(sharedPinia);

        const buyboxMountPoint =
            buyBoxAppElement.querySelector('[data-vue-mount]') || buyBoxAppElement;
        buyboxApp.mount(buyboxMountPoint);
    }

    // QW5: przelacznik Care+ na karcie (tabela jest w Twigu)
    const pdpCareElement = document.querySelector('#pdp-care-app');

    if (pdpCareElement) {
        try {
            const PdpCareApp = await import(
                /* webpackChunkName: "PdpCareApp" */ '@vuejs/apps/product/PdpCareApp.vue'
            );

            const pdpCareApp = createVueApp(PdpCareApp.default);
            pdpCareApp.use(sharedPinia);
            pdpCareApp.mount(pdpCareElement);
        } catch (e) {
            console.error(e);
        }
    }

    const stickyBuyBarElement = document.querySelector('#sticky-buy-bar-app');

    if (stickyBuyBarElement) {
        try {
            const StickyBuyBarApp = await import(
                /* webpackChunkName: "StickyBuyBarApp" */ '@vuejs/apps/product/StickyBuyBarApp.vue'
            );

            const stickyBuyBarApp = createVueApp(StickyBuyBarApp.default);
            stickyBuyBarApp.use(sharedPinia);
            stickyBuyBarApp.mount(stickyBuyBarElement);
        } catch (e) {
            console.error(e);
        }
    }

    const voucherAppElement = document.querySelector('#voucher-app');

    if (voucherAppElement) {
        const VoucherApp = await import(
            /* webpackChunkName: "VoucherApp" */ '@vuejs/apps/product/VoucherApp.vue'
        );

        const voucherApp = createVueApp(VoucherApp.default);
        voucherApp.use(sharedPinia);

        const voucherMountPoint =
            voucherAppElement.querySelector('[data-vue-mount]') || voucherAppElement;
        voucherApp.mount(voucherMountPoint);
    }

    const ringQuickOptionsElement = document.querySelector('#ring-quick-options-app');

    if (ringQuickOptionsElement) {
        const RingQuickOptionsApp = await import(
            /* webpackChunkName: "RingQuickOptionsApp" */ '@vuejs/apps/product/RingQuickOptionsApp.vue'
        );

        const ringQuickOptionsApp = createVueApp(RingQuickOptionsApp.default);
        ringQuickOptionsApp.use(sharedPinia);

        const ringQuickOptionsMountPoint =
            ringQuickOptionsElement.querySelector('[data-vue-mount]') || ringQuickOptionsElement;
        ringQuickOptionsApp.mount(ringQuickOptionsMountPoint);
    }

    const weddingRingQuickOptionsElement = document.querySelector(
        '#wedding-ring-quick-options-app'
    );

    if (weddingRingQuickOptionsElement) {
        const WeddingRingQuickOptionsApp = await import(
            /* webpackChunkName: "WeddingRingQuickOptionsApp" */ '@vuejs/apps/product/WeddingRingQuickOptionsApp.vue'
        );

        const weddingRingQuickOptionsApp = createVueApp(WeddingRingQuickOptionsApp.default);
        weddingRingQuickOptionsApp.use(sharedPinia);

        const weddingRingQuickOptionsMountPoint =
            weddingRingQuickOptionsElement.querySelector('[data-vue-mount]') ||
            weddingRingQuickOptionsElement;
        weddingRingQuickOptionsApp.mount(weddingRingQuickOptionsMountPoint);
    }

    if (document.querySelector('#promotion-landing-app')) {
        const PromotionLandingApp = await import(
            /* webpackChunkName: "EbookApp" */ '@vuejs/apps/PromotionLandingApp.vue'
        );

        const promotionLandingApp = createVueApp(PromotionLandingApp.default);

        promotionLandingApp.use(sharedPinia);

        promotionLandingApp.mount('#promotion-landing-app');
    }

    const miniCartAppElements = document.querySelectorAll('#mini-cart-app');

    if (miniCartAppElements.length) {
        const MiniCartApp = await import(
            /** webpackChunkName: "MiniCartApp" */ '@vuejs/apps/MiniCartApp.vue'
        );

        miniCartAppElements.forEach((el) => {
            const miniCartApp = createVueApp(MiniCartApp.default);

            miniCartApp.use(sharedPinia);

            miniCartApp.mount(el);
        });
    }

    const myAccountAppElements = document.querySelectorAll('.my-account-app');

    if (myAccountAppElements.length) {
        const MyAccountApp = await import(
            /** webpackChunkName: "MyAccountApp" */ '@vuejs/apps/MyAccountApp.vue'
        );
        myAccountAppElements.forEach((el) => {
            const myAccountApp = createVueApp(MyAccountApp.default);
            myAccountApp.use(sharedPinia);
            myAccountApp.mount(el);
        });
    }

    if (document.querySelector('#login-form-app')) {
        const LoginFormApp = await import(
            /* webpackChunkName: "LoginFormApp" */ '@vuejs/apps/LoginFormApp.vue'
        );

        const loginFormApp = createVueApp(LoginFormApp.default);

        loginFormApp.use(sharedPinia);

        loginFormApp.mount('#login-form-app');
    }
    if (document.querySelector('#register-form-app')) {
        const RegisterFormApp = await import(
            /* webpackChunkName: "RegisterFormApp" */ '@vuejs/apps/RegisterFormApp.vue'
        );

        const registerFormApp = createVueApp(RegisterFormApp.default);

        registerFormApp.use(sharedPinia);

        registerFormApp.mount('#register-form-app');
    }
    if (document.querySelector('#forgot-password-app')) {
        const ForgotPasswordApp = await import(
            /* webpackChunkName: "ForgotPasswordApp" */ '@vuejs/apps/ForgotPasswordApp.vue'
        );

        const forgotPasswordApp = createVueApp(ForgotPasswordApp.default);

        forgotPasswordApp.use(sharedPinia);

        forgotPasswordApp.mount('#forgot-password-app');
    }
    if (document.querySelector('#reset-password-app')) {
        const ResetPasswordApp = await import(
            /* webpackChunkName: "ResetPasswordApp" */ '@vuejs/apps/ResetPasswordApp.vue'
        );

        const resetPasswordApp = createVueApp(ResetPasswordApp.default);

        resetPasswordApp.use(sharedPinia);

        resetPasswordApp.mount('#reset-password-app');
    }
    if (document.querySelector('#order-status-app')) {
        const OrderStatusApp = await import(
            /* webpackChunkName: "OrderStatusApp" */ '@vuejs/apps/OrderStatusApp.vue'
        );

        const orderStatusApp = createVueApp(OrderStatusApp.default);

        orderStatusApp.use(sharedPinia);

        orderStatusApp.mount('#order-status-app');
    }
    if (document.querySelector('#issue-status-app')) {
        const IssueStatusApp = await import(
            /* webpackChunkName: "IssueStatusApp" */ '@vuejs/apps/IssueStatusApp.vue'
        );

        const issueStatusApp = createVueApp(IssueStatusApp.default);

        issueStatusApp.use(sharedPinia);

        issueStatusApp.mount('#issue-status-app');
    }
    if (document.querySelector('#receive-gift-lead-app')) {
        const ReceiveGiftLeadApp = await import(
            /* webpackChunkName: "ReceiveGiftLeadApp" */ '@vuejs/apps/ReceiveGiftLeadApp.vue'
        );

        const receiveGiftLeadApp = createVueApp(ReceiveGiftLeadApp.default);

        receiveGiftLeadApp.use(sharedPinia);

        receiveGiftLeadApp.mount('#receive-gift-lead-app');
    }

    if (document.querySelector('#catalog-lead-app')) {
        const CatalogLeadApp = await import(
            /* webpackChunkName: "CatalogLeadApp" */ '@vuejs/apps/CatalogLeadApp.vue'
        );

        const catalogLeadApp = createVueApp(CatalogLeadApp.default);

        catalogLeadApp.use(sharedPinia);

        catalogLeadApp.mount('#catalog-lead-app');
    }

    if (document.querySelector('#charity-return-app')) {
        const CharityReturnApp = await import(
            /* webpackChunkName: "CharityReturnApp" */ '@vuejs/apps/CharityReturnApp.vue'
        );

        const charityReturnApp = createVueApp(CharityReturnApp.default);

        charityReturnApp.use(sharedPinia);

        charityReturnApp.mount('#charity-return-app');
    }

    if (document.querySelector('#litewka-foundation-app')) {
        const LitewkaFoundationApp = await import(
            /* webpackChunkName: "LitewkaFoundationApp" */ '@vuejs/apps/LitewkaFoundationApp.vue'
        );

        const litewkaFoundationApp = createVueApp(LitewkaFoundationApp.default);

        litewkaFoundationApp.use(sharedPinia);

        litewkaFoundationApp.mount('#litewka-foundation-app');
    }

    if (document.querySelector('#collection-listing-header-app')) {
        const CollectionListingHeaderApp = await import(
            /* webpackChunkName: "ReadyProductsApp" */ '@vuejs/apps/CollectionListingHeaderApp.vue'
        );

        const collectionListingHeaderApp = createVueApp(CollectionListingHeaderApp.default);

        collectionListingHeaderApp.use(sharedPinia);

        collectionListingHeaderApp.mount('#collection-listing-header-app');
    }

    if (document.querySelector('.product-badge-app')) {
        mountProductBadges();
    }

    // Cena z kodem w kafelkach Twig - nie czekamy, reszta apek montuje sie dalej.
    mountPricesWithCode().catch((error) => console.error('[PriceWithCode]', error));

    // Synerise recommendations app
    const syneriseRecommendationsElements = document.querySelectorAll(
        '.js-vue-synerise-recommendations'
    );

    if (syneriseRecommendationsElements.length) {
        const SyneriseRecommendationsSliderApp = await import(
            /* webpackChunkName: "SyneriseRecommendationsSliderApp" */ '@vuejs/apps/SyneriseRecommendationsSliderApp.vue'
        );

        syneriseRecommendationsElements.forEach((el) => {
            const syneriseRecommendationsSliderApp = createVueApp(
                SyneriseRecommendationsSliderApp.default
            );

            syneriseRecommendationsSliderApp.provide('campaignId', el.dataset.campaignId);
            syneriseRecommendationsSliderApp.provide('label', el.dataset.label);
            syneriseRecommendationsSliderApp.use(sharedPinia);

            syneriseRecommendationsSliderApp.mount(el);
        });
    }

    // Callcenter status app
    const callcenterStatusAppElements = document.querySelectorAll('.callcenter-status-app');

    if (callcenterStatusAppElements.length) {
        const CallcenterStatusApp = await import(
            /* webpackChunkName: "CallcenterStatusApp" */ '@vuejs/apps/CallcenterStatusApp.vue'
        );

        callcenterStatusAppElements.forEach((el) => {
            const callcenterStatusApp = createVueApp(CallcenterStatusApp.default);

            callcenterStatusApp.use(sharedPinia);

            callcenterStatusApp.mount(el);
        });
    }

    // if (document.querySelector('.items-to-item-recommendations-app')) {
    //     const ItemsToItemRecommendationsApp = await import(
    //         /* webpackChunkName: "ProductsListCountApp" */ '@vuejs/apps/recombee/ItemsToItemRecommendationsApp.vue'
    //     );

    //     const itemsToItemRecommendationsAppElements = document.querySelectorAll(
    //         '.items-to-item-recommendations-app'
    //     );

    //     itemsToItemRecommendationsAppElements.forEach((el) => {
    //         const itemsToItemRecommendationsApp = createVueApp(
    //             ItemsToItemRecommendationsApp.default
    //         );

    //         itemsToItemRecommendationsApp.provide('productId', el.dataset.productId);

    //         const recombeeConfiguration = JSON.parse(el.dataset.recombeeConfiguration || '{}');

    //         if (recombeeConfiguration) {
    //             itemsToItemRecommendationsApp.provide(
    //                 'recombeeConfiguration',
    //                 recombeeConfiguration
    //             );
    //         }

    //         itemsToItemRecommendationsApp.use(sharedPinia);

    //         itemsToItemRecommendationsApp.mount(el);
    //     });
    // }

    // if (document.querySelector('.items-to-user-recommendations-app')) {
    //     const ItemsToUserRecommendationsApp = await import(
    //         /* webpackChunkName: "ProductsListCountApp" */ '@vuejs/apps/recombee/ItemsToUserRecommendationsApp.vue'
    //     );

    //     const itemsToUserRecommendationsAppElements = document.querySelectorAll(
    //         '.items-to-user-recommendations-app'
    //     );

    //     itemsToUserRecommendationsAppElements.forEach((el) => {
    //         const itemsToUserRecommendationsApp = createVueApp(
    //             ItemsToUserRecommendationsApp.default
    //         );

    //         const recombeeConfiguration = JSON.parse(el.dataset.recombeeConfiguration || '{}');

    //         if (recombeeConfiguration) {
    //             itemsToUserRecommendationsApp.provide(
    //                 'recombeeConfiguration',
    //                 recombeeConfiguration
    //             );
    //         }

    //         itemsToUserRecommendationsApp.use(sharedPinia);

    //         itemsToUserRecommendationsApp.mount(el);
    //     });
    // }

    mountPaginationApps();
});

window.addEventListener('products-list-apps-mount', () => {
    mountPaginationApps();
    // Filtry podmieniaja HTML listingu - nowe kafelki dostaja swoje wyspy.
    mountPricesWithCode().catch((error) => console.error('[PriceWithCode]', error));
});

/**
 * Komunikacja między SSR a apką vue
 */
['DOMContentLoaded', 'livewire.component.rendered'].forEach((ev) => {
    window.addEventListener(ev, () => {
        const elements = [...document.querySelectorAll('.js-vue-offcanvas-show')].filter(
            (el) => !el.dataset.vueOffcanvasShowListenerInitialized
        );

        elements.forEach((el) => {
            el.dataset.vueOffcanvasShowListenerInitialized = true;

            el.addEventListener('click', (e) => {
                e.preventDefault();

                const { componentName, payload } = el.dataset;

                window.dispatchEvent(
                    new CustomEvent('vue.offcanvas.show', {
                        detail: { componentName, payload: JSON.parse(payload || '{}') },
                    })
                );
            });
        });

        // promotion modules

        const promotionModulesElements = [
            ...document.querySelectorAll('.js-vue-promotion-show'),
        ].filter((el) => !el.dataset.vuePromotionShowListenerInitialized);

        promotionModulesElements.forEach((el) => {
            el.dataset.vuePromotionShowListenerInitialized = true;

            el.addEventListener('click', (e) => {
                e.preventDefault();

                const { componentName, payload, actionUrl, syneriseEventType } = el.dataset;

                if (actionUrl) {
                    window.location.href = actionUrl;

                    return;
                }

                const templateCode = window.PROMOTION_MODAL?.promotion_template?.code;

                if (!templateCode) {
                    return;
                }

                const parsedPayload = JSON.parse(payload || '{}');
                if (syneriseEventType) {
                    parsedPayload.syneriseEventType = syneriseEventType;
                }

                if (templateCode === 'basic_modal') {
                    window.dispatchEvent(
                        new CustomEvent('vue.modal.show', {
                            detail: {
                                componentName: componentName.replace('Offcanvas', 'Modal'),
                                payload: parsedPayload,
                            },
                        })
                    );
                } else {
                    window.dispatchEvent(
                        new CustomEvent('vue.offcanvas.show', {
                            detail: { componentName, payload: parsedPayload },
                        })
                    );
                }
            });
        });
    });
});

// lozad

const appsToLazyLoad = [
    {
        selector: '#product-reviews-app',
        getRootComponent: () =>
            import(
                /* webpackChunkName: "ProductReviewsApp" */ '@vuejs/apps/product/ProductReviewsApp.vue'
            ),
        getGlobalState: () => ({
            productDTO: window.PRODUCT_DTO,
        }),
    },
    {
        selector: '#product-fast-actions',
        getRootComponent: () =>
            import(
                /* webpackChunkName: "ProductFastActionsApp" */ '@vuejs/apps/product/ProductFastActionsApp.vue'
            ),
        getGlobalState: () => ({
            productDTO: window.PRODUCT_DTO,
        }),
    },
];
appsToLazyLoad.forEach((appData) => {
    const observer = lozad(appData.selector, {
        loaded: async (el) => {
            const component = await appData.getRootComponent();

            const app = createVueApp(component.default, {
                globalState: appData.getGlobalState(el),
            });

            app.use(sharedPinia);

            app.mount(el);
        },
    });

    document.addEventListener('DOMContentLoaded', () => {
        observer.observe();
    });
});

/**
 * LISTING RECOMMENDATIONS APP
 */

// let hasLoadedRecommendations = false;

window.addEventListener('products-list-loaded', async (e) => {
    // if (document.querySelector('#last-seen-product-app')) {
    //     const LastSeenProductApp = await import(
    //         /* webpackChunkName: "ProductsListCountApp" */ '@vuejs/apps/LastSeenProductApp.vue'
    //     );
    //     const lastSeenProductApp = createVueApp(LastSeenProductApp.default);
    //     lastSeenProductApp.use(sharedPinia);
    //     lastSeenProductApp.mount('#last-seen-product-app');
    // }
    // const listingRecommendationsAppElements = document.querySelectorAll(
    //     '.listing-recommendations-app'
    // );
    // if (listingRecommendationsAppElements.length && !hasLoadedRecommendations) {
    //     const ListingRecommendationsApp = await import(
    //         /* webpackChunkName: "ProductsListCountApp" */ '@vuejs/apps/recombee/ListingRecommendationsApp.vue'
    //     );
    //     listingRecommendationsAppElements.forEach((el) => {
    //         const listingRecommendationsApp = createVueApp(ListingRecommendationsApp.default);
    //         const recombeeConfiguration = JSON.parse(el.dataset.recombeeConfiguration || '{}');
    //         if (recombeeConfiguration) {
    //             listingRecommendationsApp.provide('recombeeConfiguration', recombeeConfiguration);
    //         }
    //         listingRecommendationsApp.use(sharedPinia);
    //         listingRecommendationsApp.mount(el);
    //     });
    // }
    // if (hasLoadedRecommendations) {
    //     // window.dispatchEvent(new CustomEvent('products-list-recommendations-loaded'));
    //     listingRecommendationsAppElements.forEach((el) => {
    //         el.remove();
    //     });
    // }
    // hasLoadedRecommendations = true;
});

/**
 * PRODUCTS PRESENTATION OFFCANVAS BRIDGE
 * Opens the Vue offcanvas from SSR-rendered marketing box buttons
 */
window.addEventListener('open-products-presentation', async (e) => {
    const { useSharedOffcanvas } = await import('@vuejs/composables/useSharedOffcanvas');
    const offcanvasStore = useSharedOffcanvas();
    offcanvasStore.show('category/ProductsPresentationOffcanvas', {
        products: e.detail.products,
    });
});

/**
 * SHARED OFFCANVAS BRIDGE
 * Otwiera dowolny offcanvas z innego bundla (np. landing w Twigu):
 * window.dispatchEvent(new CustomEvent('open-shared-offcanvas', { detail: { name, payload } }))
 * name = sciezka wzgledem resources/vue/offcanvas, np. 'lead/HighJewelleryOffcanvas'.
 */
window.addEventListener('open-shared-offcanvas', async (e) => {
    const { useSharedOffcanvas } = await import('@vuejs/composables/useSharedOffcanvas');
    useSharedOffcanvas().show(e.detail.name, e.detail.payload);
});

/**
 * PRODUCT SETS APP (sekcja "Zestawy" na karcie produktowej)
 *
 * Celowo osobny listener, a nie dopisek do wspolnego bloku DOMContentLoaded -
 * tamten jest jednym async listenerem z seria `await import(...)`, wiec rzut
 * w naszym imporcie zabilby wszystkie apki montowane po nim (m.in. mini-cart
 * i offcanvasy).
 */
window.addEventListener('DOMContentLoaded', async () => {
    const productSetsElements = document.querySelectorAll('.js-vue-product-sets');

    if (!productSetsElements.length) {
        return;
    }

    try {
        const ProductSetsApp = await import(
            /* webpackChunkName: "ProductSetsApp" */ '@vuejs/apps/product/ProductSetsApp.vue'
        );

        productSetsElements.forEach((el) => {
            if (el.__vue_app__) {
                return;
            }

            const productSetsApp = createVueApp(ProductSetsApp.default);

            productSetsApp.provide('productId', el.dataset.productId);
            productSetsApp.use(sharedPinia);

            productSetsApp.mount(el);
        });
    } catch (error) {
        console.warn('[product-sets] nie udalo sie zamontowac sekcji zestawow', error);
    }
});
